import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import './Login.css';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Registration() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // Mirror of the backend's `deriveDatabasename` sanitization
  // (Section 10.1 of the multi-tenancy backend journal):
  // - strip every character outside [A-Za-z0-9_]
  // - the sanitized result must be 2-30 chars so `tenant_<sanitized>`
  //   fits MongoDB's 38-char DB name limit.
  const sanitizeCompanyName = (raw) => (raw || '').replace(/[^A-Za-z0-9_]/g, '');

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      newErrors.password = passwordErrors[0]; // Show first error
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.length > 50) {
      newErrors.companyName = 'Company name must be 50 characters or less';
    } else {
      const sanitized = sanitizeCompanyName(formData.companyName);
      if (sanitized.length < 2 || sanitized.length > 30) {
        newErrors.companyName =
          'After removing spaces and punctuation, the company name must be 2-30 letters / digits / underscores';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      // Configure axios if baseURL is not set
      if (!axios.defaults.baseURL) {
        axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || '';
      }

      const response = await axios.post('/users/addNewUser', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 200 || response.status === 201) {
        const tenant = response.data?.tenant;
        const tenantLabel = tenant?.statuslabel || tenant?.databasename;
        setSuccessMessage(
          tenantLabel
            ? `Tenant "${tenantLabel}" created. Redirecting to login...`
            : 'Registration successful! Redirecting to login...'
        );

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }

    } catch (error) {
      console.error('Registration error:', error);

      // Log detailed error information for debugging
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      }

      // Handle different error scenarios
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 422) {
          // Handle validation errors
          const validationErrors = error.response.data?.errors || error.response.data?.details;
          if (validationErrors) {
            console.error('Validation errors:', validationErrors);
            setErrors({ submit: `Validation error: ${JSON.stringify(validationErrors)}` });
          } else {
            const errorMessage = error.response.data?.message ||
                               error.response.data?.error ||
                               'Invalid data provided. Please check your input.';
            setErrors({ submit: errorMessage });
          }
        } else if (error.response.status === 409) {
          // Either the email is already registered, or the derived tenant
          // database name collides with an existing tenant.
          const message = error.response.data?.error ||
                          error.response.data?.message ||
                          'That email or company name is already in use. Please try another.';
          setErrors({ submit: message });
        } else {
          const errorMessage = error.response.data?.message ||
                             error.response.data?.error ||
                             'Registration failed. Please try again.';
          setErrors({ submit: errorMessage });
        }
      } else if (error.request) {
        // The request was made but no response was received
        setErrors({ submit: 'Unable to connect to server. Please check if the backend is running on port 8000.' });
      } else {
        // Something happened in setting up the request that triggered an Error
        setErrors({ submit: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrength = () => {
    const password = formData.password;
    if (!password) return null;

    const validations = validatePassword(password);
    const strength = 4 - validations.length;

    if (strength === 4) return { text: 'Strong', color: '#27ae60' };
    if (strength === 3) return { text: 'Good', color: '#f39c12' };
    if (strength === 2) return { text: 'Fair', color: '#e67e22' };
    return { text: 'Weak', color: '#e74c3c' };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="login-page">
      <div className="login-container" style={{ maxWidth: '500px' }}>
        <div className="login-header">
          <h1 className="login-title">Create Account</h1>
          <p className="login-subtitle">Please fill in your information to register</p>
        </div>

        {successMessage && (
          <div style={{
            color: '#27ae60',
            backgroundColor: '#d5f4e6',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {successMessage}
          </div>
        )}

        {errors.submit && (
          <div className="error-message">{errors.submit}</div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div style={{ display: 'flex', gap: '12px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <input
                className="form-input"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="First Name"
                required
              />
              {errors.firstName && (
                <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.firstName}
                </span>
              )}
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <input
                className="form-input"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Last Name"
                required
              />
              {errors.lastName && (
                <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.lastName}
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <input
              className="form-input"
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              placeholder="Company / Tenant Name"
              required
              maxLength={50}
            />
            {(() => {
              const sanitized = sanitizeCompanyName(formData.companyName);
              if (!formData.companyName) return null;
              return (
                <div style={{
                  marginTop: '6px',
                  fontSize: '11px',
                  color: '#7f8c8d'
                }}>
                  Your tenant database will be named: <code>tenant_{sanitized || '???'}</code>
                </div>
              );
            })()}
            {errors.companyName && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.companyName}
              </span>
            )}
          </div>

          <div className="form-group">
            <input
              className="form-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email address (will be your username)"
              required
              autoComplete="username"
            />
            {errors.email && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.email}
              </span>
            )}
          </div>

          <div className="form-group">
            <input
              className="form-input"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Password"
              required
              autoComplete="new-password"
            />
            {passwordStrength && (
              <div style={{
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Password strength:</span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: passwordStrength.color
                }}>
                  {passwordStrength.text}
                </span>
              </div>
            )}
            {errors.password && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.password}
              </span>
            )}
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#7f8c8d'
            }}>
              Password must contain:
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                <li style={{ color: formData.password.length >= 12 ? '#27ae60' : 'inherit' }}>
                  At least 12 characters
                </li>
                <li style={{ color: /[A-Z]/.test(formData.password) ? '#27ae60' : 'inherit' }}>
                  One uppercase letter
                </li>
                <li style={{ color: /[0-9]/.test(formData.password) ? '#27ae60' : 'inherit' }}>
                  One number
                </li>
                <li style={{ color: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password) ? '#27ae60' : 'inherit' }}>
                  One special character
                </li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <input
              className="form-input"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm Password"
              required
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <LoadingSpinner size="small" />
            ) : 'Create Account'}
          </button>

          <div className="login-divider">
            <span>or</span>
          </div>

          <div className="social-login">
            <button type="button" className="social-button google">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <p className="signup-link">
            Already have an account? <a href="/login">Sign in</a>
          </p>
        </form>
      </div>
    </div>
  );
}